/**
 * The "what is this list actually filtered by" sentence.
 *
 * Extracted from `ReportFilterBar`, which grew it first, because the issuance
 * list now needs the same thing for the same reason and a second
 * implementation would drift. The reports bar hides filters behind a "More
 * filters" disclosure; the issuance list hides ALL of them behind a closed
 * drawer. Both leave a user looking at a short table with no visible cause,
 * and both answer it here.
 *
 * Rendered as a plain sentence, never as chips. Chips read as removable, and
 * these are not: they describe the APPLIED query, which only Apply and Clear
 * change. A dead affordance is worse than none.
 *
 * The text always describes the applied filters, never the draft. A summary
 * that read the draft would claim the table is filtered by something the user
 * staged and never applied — which is the one thing this row exists to rule
 * out.
 */

const rowStyle = {
  marginTop: 10,
  paddingTop: 10,
  borderTop: '1px solid #F1F2F4',
  fontSize: 12,
  lineHeight: 1.5,
};

/** Standalone above a table rather than tucked under the controls that set
 *  it: no divider to attach to, and it needs to sit off the table's top edge. */
const standaloneStyle = {
  marginBottom: 10,
  fontSize: 12,
  lineHeight: 1.5,
};

const labelStyle = { color: '#6B7280' };
const textStyle = { color: '#1F1F1F', fontWeight: 500 };

/**
 * "Label: value" for one multi-select's applied selection, or null when it
 * holds nothing.
 *
 * One option is named; several are counted. Naming them all is what pushes
 * this row onto three lines the moment anyone uses select-all, and the count
 * is the part that actually answers "why are rows missing".
 */
export const summariseSelection = (label, selected) => {
  if (!selected || selected.length === 0) return null;
  return selected.length === 1
    ? `${label}: ${selected[0].label}`
    : `${label}: ${selected.length} selected`;
};

/** Same shape for a single-select holding one option. */
export const summariseOption = (label, option) => (option ? `${label}: ${option.label}` : null);

/**
 * @param {string} lead Sentence opener — "Also filtering by:" when some
 *   filters are still visible beside it, "Filtered by:" when none are.
 * @param {Array<string|null>} entries Summary fragments; falsy ones are
 *   dropped, so callers can map over fields without pre-filtering.
 * @param {boolean} [divider] Draw the hairline above (default: true). Off for
 *   a standalone row that is not continuing a panel.
 */
const AppliedFilterSummary = ({ lead, entries = [], divider = true }) => {
  const shown = entries.filter(Boolean);
  if (shown.length === 0) return null;
  return (
    <div style={divider ? rowStyle : standaloneStyle}>
      <span style={labelStyle}>{lead} </span>
      <span style={textStyle}>{shown.join(' · ')}</span>
    </div>
  );
};

export default AppliedFilterSummary;
